/**
 * Admin Creator Match page — /admin/creator-match
 *
 * Multi-step flow:
 *  1. Upload Modash PDF + review extracted metrics
 *  2. Run match with animated loading overlay
 *  3. View results: GMV estimate + top 5 products
 *  4. Export PDF report
 *
 * Secondary tabs: Products · Calibration
 *
 * SECURITY: No Supabase queries from this component.
 *   All intel data is fetched via /api/admin/creator-match/* endpoints.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, FileText, ChevronDown, ChevronUp, Download, Send,
  Loader2, Sparkles, Package, BarChart3, Link2, Globe,
  Plus, CheckCircle2, AlertTriangle, Info, X,
} from 'lucide-react';
import { parsePdf, type ParsedModashReport } from '../lib/parsePdf';
import { PageHeader, SectionCard, StatusBadge, ScoreBar, useToast, Toast, ToastContainer } from '../components/ui';
import { API_BASE, proxyImageUrl } from '../api';
import { useDemo } from '../context/DemoContext';
import { DEMO_MATCH_RESULT } from '../demo/data';

type ParsedCreatorMetrics = ParsedModashReport;

// ── API helpers ───────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}/admin/creator-match${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...((opts.headers ?? {}) as Record<string, string>) },
    ...opts,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText })) as { error?: string; code?: string };
    if (res.status === 402 || body.code === 'not_enough_credits') {
      throw new Error('Modash API credits exhausted. Switch to PDF Import mode or contact support to top up credits.');
    }
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ── Types ─────────────────────────────────────────────────────────────────

interface FactorEntry { weight: number; raw_score: number; contribution: number; why: string }
interface FactorBreakdown {
  category: FactorEntry; tags: FactorEntry; age: FactorEntry; price: FactorEntry;
  margin: FactorEntry; complexity: FactorEntry; proof: FactorEntry;
  interest_affinity: FactorEntry; gender_penalty: FactorEntry; total_score: number;
}
interface TopProduct {
  rank: number; product_id: number; title: string; short_description: string | null;
  primary_image_url: string | null; price_min: number; price_max: number; currency: string;
  vertical: string; category: string; tags: string[]; score: number; score_pct: number;
  factor_breakdown: FactorBreakdown;
  gmv_low?: number; gmv_expected?: number; gmv_high?: number;
}
interface GmvResult {
  aov_estimated: number; buyers_low: number; buyers_expected: number; buyers_high: number;
  gmv_low: number; gmv_expected: number; gmv_high: number; tier: number; confidence: number;
  assumptions: Record<string, unknown>;
}
interface CollaborationBrand {
  name: string; username?: string | null; domain?: string | null; logo_url?: string | null;
  category: string; collaboration_type: string; post_count: number;
  total_likes?: number | null; total_comments?: number | null; total_views?: number | null;
}
interface AnalyzeResult {
  creator: { id: number; platform: string; handle: string; profile_url: string; profile_image_url?: string | null };
  analysis: {
    followers: number | null; engagement_rate: number | null;
    avg_views: number | null; avg_likes: number | null; avg_comments?: number | null;
    following_count?: number | null; posts_count?: number | null;
    audience_age_min: number | null; audience_age_max: number | null;
    audience_gender_affinity: string; primary_category: string | null;
    secondary_categories?: string[] | null;
    geo_top_countries: Array<{ country: string; pct: number }> | null;
    credibility_score?: number | null;
    paid_post_performance?: number | null;
    gender_distribution?: { male?: number; female?: number } | null;
    age_distribution?: Array<{ label: string; followers_pct: number | null }> | null;
    geo_distribution?: Array<{ label: string; followers_pct: number | null }> | null;
    audience_languages?: Array<{ code: string; name?: string; weight: number }> | null;
    audience_cities?: Array<{ code?: string; name?: string; weight: number }> | null;
    audience_ethnicities?: Array<{ code: string; name?: string; weight: number }> | null;
    interests_ranked?: Array<{ label: string; followers_pct: number | null }> | null;
    brand_affinity_ranked?: Array<{ label: string; followers_pct: number | null }> | null;
    derived_features?: Record<string, unknown> | null;
    collaboration_brands?: CollaborationBrand[] | null;
    notes?: Record<string, unknown> | null;
    provider: string; is_mock: boolean;
  };
  match_run_id: number; top_products: TopProduct[]; gmv: GmvResult; is_mock: boolean;
}
interface ProductItem {
  id: number; title: string; short_description: string | null; primary_image_url: string | null;
  price_min: number; price_max: number; currency: string; vertical: string; category: string;
  subcategory: string | null; tags: string[]; impulse_score: number; margin_score: number; proof_score: number; is_active: boolean;
}
interface CalibRow {
  id: number; platform: string; follower_tier: string; engagement_tier: string;
  category: string; geo_bias: string; conv_low: number; conv_expected: number; conv_high: number;
}
interface ScoringConfig { id: number; name: string; weights: Record<string, number> }

// ── Multi-platform types ──────────────────────────────────────────────────

interface PlatformGmvBreakdown {
  platform: string; handle: string; followers: number; engagement_rate: number;
  gmv_low: number; gmv_expected: number; gmv_high: number; assumptions: Record<string, unknown>;
}
interface MultiPlatformGmvResult {
  platform_breakdown: PlatformGmvBreakdown[];
  overlap_factor: number;
  total_gmv_low: number; total_gmv_expected: number; total_gmv_high: number;
  total_aov_estimated: number; tier: number; confidence: number;
}
interface MultiPlatformAnalyzeResult {
  platforms: Array<{
    platform: string; handle: string;
    creator: { id: number; platform: string; handle: string; profile_url: string };
    analysis: {
      id: number; followers: number | null; engagement_rate: number | null; avg_views: number | null;
      audience_gender_affinity: string; primary_category: string | null;
      geo_top_countries: Array<{ country: string; pct: number }> | null; provider: string;
    };
  }>;
  match_run_id: number; top_products: TopProduct[]; gmv: MultiPlatformGmvResult; is_multi: true;
}
type AnyAnalyzeResult = AnalyzeResult | MultiPlatformAnalyzeResult;

function isMultiResult(r: AnyAnalyzeResult): r is MultiPlatformAnalyzeResult {
  return 'is_multi' in r && r.is_multi === true;
}

interface PlatformFormEntry {
  platform: 'instagram' | 'tiktok' | 'youtube';
  handle: string;
  pdfFile: File | null;
  parsedMetrics: ParsedCreatorMetrics | null;
  enabled: boolean;
}

const PLATFORM_URL_TEMPLATES: Record<string, (handle: string) => string> = {
  instagram: (h) => `https://www.instagram.com/${h}/`,
  tiktok:    (h) => `https://www.tiktok.com/@${h}`,
  youtube:   (h) => `https://www.youtube.com/@${h}`,
};

const PLATFORM_META: Record<string, { label: string; color: string; icon: string }> = {
  instagram: { label: 'Instagram', color: '#E4405F', icon: 'IG' },
  tiktok:    { label: 'TikTok',    color: '#00f2ea', icon: 'TT' },
  youtube:   { label: 'YouTube',   color: '#FF0000', icon: 'YT' },
};

// ── Utility ───────────────────────────────────────────────────────────────

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '—';
  return n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(0)}K` : String(n);
}
function fmtMoney(n: number) {
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

// ── GMV Tier Badge ────────────────────────────────────────────────────────

function GmvTierBadge({ tier }: { tier: number }) {
  const map: Record<number, { label: string; variant: 'success' | 'info' | 'neutral' }> = {
    1: { label: 'Tier 1 · Top', variant: 'success' },
    2: { label: 'Tier 2 · Mid', variant: 'info' },
    3: { label: 'Tier 3 · Entry', variant: 'neutral' },
  };
  const t = map[tier] ?? map[3];
  return <StatusBadge variant={t.variant}>{t.label}</StatusBadge>;
}

// ── Score Chip ────────────────────────────────────────────────────────────

function ScoreChip({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const color = pct >= 70 ? 'text-gold bg-gold/10 border-gold/20'
              : pct >= 50 ? 'text-amber-300 bg-amber-400/10 border-amber-400/20'
              : 'text-white/50 bg-white/[0.04] border-white/[0.10]';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold border ${color}`}
      style={{ borderRadius: '2px' }}>
      {pct}%
    </span>
  );
}

// ── Loading Overlay ───────────────────────────────────────────────────────

const MATCH_STEPS = [
  'Searching profiles',
  'Fetching audience data',
  'Analyzing collaborations',
  'Scoring products',
  'Estimating GMV',
];

function MatchingOverlay({ onCancel }: { onCancel: () => void }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timers = MATCH_STEPS.map((_, i) =>
      setTimeout(() => setStep(i), i * 900)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col items-center justify-center min-h-[420px] text-center"
    >
      <div className="text-center">
        <div
          className="w-14 h-14 flex items-center justify-center mx-auto mb-5"
          style={{ backgroundColor: 'rgba(255,212,0,0.10)', border: '1px solid rgba(255,212,0,0.18)', borderRadius: '4px' }}
        >
          <Sparkles className="w-7 h-7 animate-pulse" style={{ color: '#FFD400' }} />
        </div>
        <p className="text-base font-semibold text-white mb-1">Running Match…</p>
        <p className="text-sm mb-6" style={{ color: '#FFD400' }}>{MATCH_STEPS[step]}</p>
        <div className="flex items-center gap-1.5 justify-center mb-6">
          {MATCH_STEPS.map((_, i) => (
            <motion.div
              key={i}
              animate={{ opacity: i <= step ? 1 : 0.2, scale: i === step ? 1.15 : 1 }}
              transition={{ duration: 0.3 }}
              className="h-1 transition-all"
              style={{
                backgroundColor: i <= step ? '#FFD400' : 'rgba(255,255,255,0.15)',
                width: i === step ? '24px' : '10px',
                borderRadius: '1px',
              }}
            />
          ))}
        </div>
        <button onClick={onCancel} className="text-xs text-white/30 hover:text-white/60 transition-colors">
          Cancel
        </button>
      </div>
    </motion.div>
  );
}

// ── Platform Badge ─────────────────────────────────────────────────────────

function PlatformBadge({ platform }: { platform: string }) {
  const meta = PLATFORM_META[platform] ?? { label: platform, color: '#888', icon: '?' };
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
      style={{ backgroundColor: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}30`, borderRadius: '2px' }}>
      {meta.icon}
    </span>
  );
}

// ── Results: Creator + GMV (supports both single and multi-platform) ──────

function ResultsPanel({
  result,
  onExportPdf,
  onOpenFullReport,
}: {
  result: AnyAnalyzeResult;
  onExportPdf: () => void;
  onOpenFullReport: () => void;
}) {
  const multi = isMultiResult(result);
  const top_products = result.top_products;
  const [expanded, setExpanded] = useState<number | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [imgErrors, setImgErrors] = useState<Set<number>>(new Set());

  const gmvLow = multi ? result.gmv.total_gmv_low : result.gmv.gmv_low;
  const gmvExpected = multi ? result.gmv.total_gmv_expected : result.gmv.gmv_expected;
  const gmvHigh = multi ? result.gmv.total_gmv_high : result.gmv.gmv_high;
  const gmvTier = multi ? result.gmv.tier : result.gmv.tier;
  const gmvConfidence = multi ? result.gmv.confidence : result.gmv.confidence;

  return (
    <div className="space-y-4">
      {!multi && (result as AnalyzeResult).is_mock && (
        <div className="flex items-center gap-2 p-3 text-amber-400 text-xs"
          style={{ backgroundColor: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)', borderRadius: '2px' }}>
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Using mock data (MODASH_API_TOKEN not set). Results are synthetic.
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-xs text-white/30">Run #{result.match_run_id}</p>
          {multi && (
            <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
              style={{ backgroundColor: 'rgba(255,212,0,0.08)', color: '#FFD400', border: '1px solid rgba(255,212,0,0.20)', borderRadius: '2px' }}>
              Multi-platform
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowSendModal(true)} className="btn-ghost text-xs flex items-center gap-1.5 py-1.5">
            <Send className="w-3.5 h-3.5" /> Send Report
          </button>
          <button onClick={onOpenFullReport} className="btn-secondary text-xs flex items-center gap-1.5 py-1.5 px-3">
            <Sparkles className="w-3.5 h-3.5" /> Open full report
          </button>
          <button onClick={onExportPdf} className="btn-primary text-xs flex items-center gap-1.5 py-1.5 px-4">
            <Download className="w-3.5 h-3.5" /> Download legacy PDF
          </button>
        </div>
      </div>

      {/* Creator overview */}
      {multi ? (
        <SectionCard title="Creator Overview" actions={
          <div className="flex items-center gap-1">
            {result.platforms.map(p => <PlatformBadge key={p.platform} platform={p.platform} />)}
          </div>
        }>
          <div className="space-y-3">
            {result.platforms.map(p => (
              <div key={p.platform} className="p-3" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}>
                <div className="flex items-center gap-2 mb-2">
                  <PlatformBadge platform={p.platform} />
                  <span className="text-sm font-semibold text-white">@{p.handle}</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-[10px] text-white/35 uppercase tracking-wider">Followers</p>
                    <p className="text-sm font-semibold text-white">{fmtNum(p.analysis.followers ?? 0)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/35 uppercase tracking-wider">Engagement</p>
                    <p className="text-sm font-semibold text-white">{p.analysis.engagement_rate ? `${(p.analysis.engagement_rate * 100).toFixed(2)}%` : '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/35 uppercase tracking-wider">Category</p>
                    <p className="text-sm font-semibold text-white">{p.analysis.primary_category ?? '—'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      ) : (
        <SectionCard title="Creator Overview">
          {(result as AnalyzeResult).creator.profile_image_url && (
            <div className="flex items-center gap-4 px-5 pt-5 pb-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <img
                src={(result as AnalyzeResult).creator.profile_image_url!}
                alt={(result as AnalyzeResult).creator.handle}
                referrerPolicy="no-referrer"
                className="w-16 h-16 rounded-full object-cover shrink-0"
                style={{ border: '2px solid rgba(255,212,0,0.3)' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
              <div>
                <p className="font-semibold text-white">@{(result as AnalyzeResult).creator.handle}</p>
                <p className="text-xs text-white/40 capitalize mt-0.5">{(result as AnalyzeResult).creator.platform}</p>
              </div>
            </div>
          )}
          <div className={`grid grid-cols-3 gap-4 p-5`}>
            {(() => {
              const a = (result as AnalyzeResult).analysis;
              const cr = (result as AnalyzeResult).creator;
              const credPct = a.credibility_score != null ? Math.round(a.credibility_score * 100) : null;
              const credColor = a.credibility_score != null
                ? a.credibility_score >= 0.80 ? '#4ade80'
                : a.credibility_score >= 0.65 ? '#facc15'
                : '#f87171'
                : null;
              const stats = [
                { label: 'Platform', value: cr.platform.charAt(0).toUpperCase() + cr.platform.slice(1) },
                { label: 'Handle', value: `@${cr.handle}` },
                { label: 'Followers', value: fmtNum(a.followers ?? 0) },
                { label: 'Engagement', value: a.engagement_rate ? `${(a.engagement_rate * 100).toFixed(2)}%` : '—' },
                { label: 'Avg Views', value: fmtNum(a.avg_views) },
                { label: 'Avg Likes', value: fmtNum(a.avg_likes) },
                { label: 'Avg Comments', value: fmtNum(a.avg_comments) },
                { label: 'Following', value: fmtNum(a.following_count) },
                { label: 'Posts', value: fmtNum(a.posts_count) },
                { label: 'Age Range', value: a.audience_age_min ? `${a.audience_age_min}–${a.audience_age_max}` : '—' },
                { label: 'Gender', value: a.audience_gender_affinity },
                { label: 'Category', value: a.primary_category ?? '—' },
              ];
              return <>
                {stats.map(({ label, value }) => (
                  <div key={label}>
                    <p className="text-[11px] font-semibold text-white/35 uppercase tracking-wider mb-0.5">{label}</p>
                    <p className="text-sm font-semibold text-white truncate">{value}</p>
                  </div>
                ))}
                {credPct != null && (
                  <div className="col-span-3 mt-1 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="text-[11px] font-semibold text-white/35 uppercase tracking-wider mb-2">Audience Quality (Real Followers)</p>
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-bold" style={{ color: credColor! }}>{credPct}%</span>
                      <div className="flex-1 h-2 rounded-sm overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                        <div className="h-full" style={{ width: `${credPct}%`, backgroundColor: credColor! }} />
                      </div>
                      <span className="text-xs text-white/40">{100 - credPct}% suspected fake</span>
                    </div>
                    {a.credibility_score! < 0.75 && (
                      <p className="text-[10px] mt-1" style={{ color: '#f87171' }}>Below 75% benchmark — recommend additional vetting</p>
                    )}
                  </div>
                )}
                {a.paid_post_performance != null && (
                  <div className="col-span-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '10px' }}>
                    <p className="text-[11px] font-semibold text-white/35 uppercase tracking-wider mb-1">Paid Post Performance</p>
                    <p className="text-sm" style={{ color: a.paid_post_performance >= 0.85 ? '#4ade80' : a.paid_post_performance >= 0.65 ? '#facc15' : '#f87171' }}>
                      <span className="font-bold">{Math.round(a.paid_post_performance * 100)}%</span>
                      <span className="text-white/40 font-normal text-xs ml-2">of organic engagement on sponsored posts</span>
                    </p>
                  </div>
                )}
              </>;
            })()}
          </div>
        </SectionCard>
      )}

      {/* Audience Insights (single platform only) */}
      {!multi && (() => {
        const a = (result as AnalyzeResult).analysis;
        const hasAudienceData = a.interests_ranked?.length || a.gender_distribution || a.brand_affinity_ranked?.length;
        if (!hasAudienceData) return null;
        return (
          <SectionCard title="Audience Insights" subtitle={`Source: ${a.provider}`}>
            <div className="grid grid-cols-2 gap-4">
              {a.gender_distribution && (
                <div>
                  <p className="text-[10px] font-semibold text-white/35 uppercase tracking-wider mb-2">Gender Split</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '1px' }}>
                      <div className="h-full" style={{ width: `${(a.gender_distribution.female ?? 0) * 100}%`, backgroundColor: '#E4405F' }} />
                    </div>
                    <span className="text-xs text-white/50 w-16 text-right">{Math.round((a.gender_distribution.female ?? 0) * 100)}% F</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-2 overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '1px' }}>
                      <div className="h-full" style={{ width: `${(a.gender_distribution.male ?? 0) * 100}%`, backgroundColor: '#4F8CFF' }} />
                    </div>
                    <span className="text-xs text-white/50 w-16 text-right">{Math.round((a.gender_distribution.male ?? 0) * 100)}% M</span>
                  </div>
                </div>
              )}
              {a.interests_ranked && a.interests_ranked.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-white/35 uppercase tracking-wider mb-2">Top Audience Interests</p>
                  <div className="space-y-1">
                    {a.interests_ranked.slice(0, 5).map(i => (
                      <div key={i.label} className="flex items-center justify-between">
                        <span className="text-xs text-white/60 truncate mr-2">{i.label}</span>
                        <span className="text-xs font-mono text-white/30 shrink-0">{i.followers_pct ? `${Math.round(i.followers_pct * 100)}%` : '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {a.brand_affinity_ranked && a.brand_affinity_ranked.length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] font-semibold text-white/35 uppercase tracking-wider mb-2">Top Audience Brand Affinity</p>
                <div className="flex flex-wrap gap-1.5">
                  {a.brand_affinity_ranked.slice(0, 8).map(b => (
                    <span key={b.label} className="px-2 py-1 text-[10px] text-white/50"
                      style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px' }}>
                      {b.label} <span className="text-white/25">{b.followers_pct ? `${Math.round(b.followers_pct * 100)}%` : ''}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>
        );
      })()}

      {/* Collaboration Brands (single platform only) */}
      {!multi && (() => {
        const collabs = (result as AnalyzeResult).analysis.collaboration_brands;
        if (!collabs?.length) return null;
        const collabTypeColor = (t: string) =>
          t === 'Ambassador' ? { bg: 'rgba(255,212,0,0.12)', text: '#FFD400' }
          : t === 'Paid'      ? { bg: 'rgba(74,222,128,0.10)', text: '#4ade80' }
          : t === 'Affiliate' ? { bg: 'rgba(96,165,250,0.10)', text: '#60a5fa' }
          : t === 'Gifted'    ? { bg: 'rgba(167,139,250,0.10)', text: '#a78bfa' }
          :                     { bg: 'rgba(255,255,255,0.04)', text: 'rgba(255,255,255,0.35)' };
        return (
          <SectionCard title="Brand Collaborations" subtitle={`${collabs.length} brand${collabs.length !== 1 ? 's' : ''} detected via Modash`}>
            <div className="space-y-1.5">
              {collabs.map(cb => {
                const tc = collabTypeColor(cb.collaboration_type);
                return (
                  <div key={cb.name} className="flex items-center gap-3 p-2.5"
                    style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{cb.name}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-[10px] text-white/30">{cb.category}</span>
                        {cb.total_likes != null && (
                          <span className="text-[10px] text-white/25">{fmtNum(cb.total_likes)} likes</span>
                        )}
                        {cb.total_comments != null && (
                          <span className="text-[10px] text-white/25">{fmtNum(cb.total_comments)} comments</span>
                        )}
                        {cb.total_views != null && (
                          <span className="text-[10px] text-white/25">{fmtNum(cb.total_views)} views</span>
                        )}
                      </div>
                    </div>
                    <span className="px-2 py-0.5 text-[10px] font-semibold shrink-0"
                      style={{ backgroundColor: tc.bg, color: tc.text, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}>
                      {cb.collaboration_type}
                    </span>
                    <span className="text-xs text-white/25 shrink-0">{cb.post_count} post{cb.post_count !== 1 ? 's' : ''}</span>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        );
      })()}

      {/* Multi-platform per-platform GMV breakdown */}
      {multi && result.gmv.platform_breakdown.length > 1 && (
        <SectionCard title="Platform GMV Breakdown" subtitle={`~${Math.round((1 - result.gmv.overlap_factor) * 100)}% audience overlap adjustment applied`}>
          <div className="space-y-2">
            {result.gmv.platform_breakdown.map(pb => (
              <div key={pb.platform} className="flex items-center gap-3 p-3"
                style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}>
                <PlatformBadge platform={pb.platform} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white">@{pb.handle}</p>
                  <p className="text-xs text-white/35">{fmtNum(pb.followers)} followers · {(pb.engagement_rate * 100).toFixed(2)}%</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold" style={{ color: '#FFD400' }}>{fmtMoney(pb.gmv_expected)}</p>
                  <p className="text-[10px] text-white/30">{fmtMoney(pb.gmv_low)} – {fmtMoney(pb.gmv_high)}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* GMV */}
      <SectionCard title={multi ? "Combined GMV Estimate" : "GMV Estimate"} actions={<><GmvTierBadge tier={gmvTier} /><span className="text-xs text-white/30 ml-2">Confidence: {gmvConfidence}%</span></>}>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="p-4 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}>
            <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">Conservative</p>
            <p className="text-lg font-bold text-white/70">{fmtMoney(gmvLow)}</p>
          </div>
          <div className="p-4 text-center" style={{ backgroundColor: 'rgba(255,212,0,0.05)', border: '1px solid rgba(255,212,0,0.18)', borderRadius: '2px' }}>
            <p className="text-[11px] uppercase tracking-wider mb-1.5" style={{ color: '#FFD400' }}>Expected</p>
            <p className="text-lg font-bold" style={{ color: '#FFD400' }}>{fmtMoney(gmvExpected)}</p>
          </div>
          <div className="p-4 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}>
            <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">Optimistic</p>
            <p className="text-lg font-bold text-white/70">{fmtMoney(gmvHigh)}</p>
          </div>
        </div>
        {multi && (
          <p className="text-xs text-white/30 mb-3">
            After ~{Math.round((1 - result.gmv.overlap_factor) * 100)}% audience overlap adjustment across {result.gmv.platform_breakdown.length} platforms
          </p>
        )}
        {!multi && (
          <details className="text-xs text-white/25">
            <summary className="cursor-pointer hover:text-white/50 flex items-center gap-1.5 transition-colors">
              <Info className="w-3 h-3" /> Assumptions
            </summary>
            <pre className="mt-2 p-3 text-white/35 font-mono whitespace-pre-wrap overflow-auto max-h-40"
              style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '2px' }}>
              {JSON.stringify((result as AnalyzeResult).gmv.assumptions, null, 2)}
            </pre>
          </details>
        )}
      </SectionCard>

      {/* Top 5 products */}
      <SectionCard title="Top 5 Matched Products">
        <div className="space-y-2">
          {top_products.map((p, i) => (
            <div key={p.product_id} className="overflow-hidden"
              style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: '2px' }}>
              <button
                className="w-full flex items-center gap-3 p-3.5 transition-colors text-left"
                onClick={() => setExpanded(expanded === i ? null : i)}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.03)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'}
              >
                <span className="text-xs font-bold text-white/20 w-5 shrink-0">#{p.rank}</span>
                {p.primary_image_url && !imgErrors.has(p.product_id) ? (
                  <img
                    src={proxyImageUrl(p.primary_image_url)!} alt={p.title}
                    className="w-12 h-12 object-cover shrink-0"
                    style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px' }}
                    onError={() => setImgErrors(prev => new Set([...prev, p.product_id]))}
                  />
                ) : (
                  <div className="w-12 h-12 flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px' }}>
                    <Package className="w-4 h-4 text-white/25" />
                  </div>
                )}
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-white truncate">{p.title}</p>
                  <p className="text-xs text-white/40">{p.vertical} · {p.category} · ${p.price_min}–${p.price_max}</p>
                  {p.gmv_expected != null && (
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] text-white/25">GMV est.</span>
                      <span className="text-[10px] text-white/35">
                        Low <span className="text-white/55 font-medium">{fmtMoney(p.gmv_low ?? 0)}</span>
                      </span>
                      <span className="text-[10px] text-white/35">
                        Realistic <span className="font-semibold" style={{ color: '#FFD400' }}>{fmtMoney(p.gmv_expected)}</span>
                      </span>
                      <span className="text-[10px] text-white/35">
                        High <span className="text-white/55 font-medium">{fmtMoney(p.gmv_high ?? 0)}</span>
                      </span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(p.tags ?? []).slice(0, 4).map(t => (
                      <span key={t} className="px-1.5 py-0.5 text-[10px]"
                        style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.45)', borderRadius: '2px' }}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <ScoreChip score={p.score} />
                  {expanded === i ? <ChevronUp className="w-4 h-4 text-white/25" /> : <ChevronDown className="w-4 h-4 text-white/25" />}
                </div>
              </button>
              <AnimatePresence>
                {expanded === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'rgba(255,255,255,0.015)' }}>
                      <p className="text-[11px] font-semibold text-white/35 uppercase tracking-wider mb-3">Score Breakdown</p>
                      <ScoreBar label="Category Match"    score={p.factor_breakdown.category.raw_score}  weight={p.factor_breakdown.category.weight}  why={p.factor_breakdown.category.why} />
                      <ScoreBar label="Tag Overlap"       score={p.factor_breakdown.tags.raw_score}      weight={p.factor_breakdown.tags.weight}      why={p.factor_breakdown.tags.why} />
                      <ScoreBar label="Interest Affinity" score={p.factor_breakdown.interest_affinity.raw_score} weight={p.factor_breakdown.interest_affinity.weight} why={p.factor_breakdown.interest_affinity.why} />
                      <ScoreBar label="Age Fit"           score={p.factor_breakdown.age.raw_score}       weight={p.factor_breakdown.age.weight}       why={p.factor_breakdown.age.why} />
                      <ScoreBar label="Price Fit"         score={p.factor_breakdown.price.raw_score}     weight={p.factor_breakdown.price.weight}     why={p.factor_breakdown.price.why} />
                      <ScoreBar label="Margin"            score={p.factor_breakdown.margin.raw_score}    weight={p.factor_breakdown.margin.weight}    why={p.factor_breakdown.margin.why} />
                      <ScoreBar label="Social Proof"      score={p.factor_breakdown.proof.raw_score}     weight={p.factor_breakdown.proof.weight}     why={p.factor_breakdown.proof.why} />
                      <ScoreBar label="Complexity"        score={p.factor_breakdown.complexity.raw_score} weight={p.factor_breakdown.complexity.weight} why={p.factor_breakdown.complexity.why} penalty />
                      {p.factor_breakdown.gender_penalty?.raw_score !== 0 && (
                        <ScoreBar label="Gender Mismatch" score={Math.abs(p.factor_breakdown.gender_penalty?.raw_score ?? 0)} weight={1} why={p.factor_breakdown.gender_penalty?.why ?? ''} penalty />
                      )}
                      <div className="mt-3 pt-3 flex justify-between text-xs font-bold"
                        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <span className="text-white/40">Total Score</span>
                        <span className="text-white">{Math.round(p.factor_breakdown.total_score * 100)}%</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Send modal */}
      <AnimatePresence>
        {showSendModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ backgroundColor: 'rgba(11,11,11,0.80)', backdropFilter: 'blur(4px)' }}
            onClick={() => setShowSendModal(false)}
          >
            <motion.div
              initial={{ scale: 0.97, y: 8 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.97, y: 8 }}
              transition={{ duration: 0.18 }}
              className="card p-6 max-w-sm w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(255,212,0,0.10)', color: '#FFD400', border: '1px solid rgba(255,212,0,0.18)', borderRadius: '2px' }}>
                  <Send className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-sm">Send Report</h3>
                  <p className="text-xs text-white/35">Coming soon</p>
                </div>
              </div>
              <p className="text-sm text-white/45 mb-4 leading-relaxed">
                Report delivery via email and SMS is coming in the next release.
                Export the PDF now and share it manually.
              </p>
              <div className="flex gap-2">
                <button onClick={onExportPdf} className="btn-primary text-xs flex-1">
                  <Download className="w-4 h-4" /> Download PDF
                </button>
                <button onClick={() => setShowSendModal(false)} className="btn-ghost text-xs px-3">Dismiss</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Analyze Tab — URL/Handle primary, PDF fallback ─────────────────────────

function AnalyzeTab({ demoMode = false }: { demoMode?: boolean }) {
  const navigate = useNavigate();
  type InputMode = 'url' | 'pdf';
  const [inputMode, setInputMode] = useState<InputMode>('url');

  // URL/handle input state
  const [profileUrl, setProfileUrl] = useState('');
  const [forceRefresh, setForceRefresh] = useState(false);

  // PDF import state (fallback)
  const [platformInputs, setPlatformInputs] = useState<PlatformFormEntry[]>([
    { platform: 'instagram', handle: '', pdfFile: null, parsedMetrics: null, enabled: true },
    { platform: 'tiktok',    handle: '', pdfFile: null, parsedMetrics: null, enabled: false },
    { platform: 'youtube',   handle: '', pdfFile: null, parsedMetrics: null, enabled: false },
  ]);
  const [parsing, setParsing]           = useState<string | null>(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [result, setResult]             = useState<AnyAnalyzeResult | null>(demoMode ? DEMO_MATCH_RESULT as unknown as AnalyzeResult : null);
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const { toasts, addToast, removeToast } = useToast();

  // Sync demo result when demoMode toggles
  useEffect(() => {
    if (demoMode) {
      setResult(DEMO_MATCH_RESULT as unknown as AnalyzeResult);
    } else {
      setResult(null);
    }
  }, [demoMode]);

  // Auto-detect platform from URL
  const detectedPlatform = (() => {
    const lower = profileUrl.toLowerCase();
    if (lower.includes('instagram.com')) return 'Instagram';
    if (lower.includes('tiktok.com'))    return 'TikTok';
    if (lower.includes('youtube.com'))   return 'YouTube';
    return null;
  })();

  const updatePlatform = useCallback((platform: string, update: Partial<PlatformFormEntry>) => {
    setPlatformInputs(prev => prev.map(p => p.platform === platform ? { ...p, ...update } : p));
  }, []);

  const processFile = useCallback(async (platform: string, file: File) => {
    if (!file.name.endsWith('.pdf')) { setError('Please upload a PDF file'); return; }
    setParsing(platform);
    setError(null);
    try {
      const metrics = await parsePdf(file);
      const updates: Partial<PlatformFormEntry> = { pdfFile: file, parsedMetrics: metrics };
      if (metrics.handle) updates.handle = metrics.handle;
      updatePlatform(platform, updates);
      addToast(`${PLATFORM_META[platform]?.label} PDF parsed — ${Object.keys(metrics.debug.extractedSections).length} sections`, 'success');
    } catch (err) {
      setError(`PDF parse failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setParsing(null);
    }
  }, [addToast, updatePlatform]);

  function buildMetricsPayload(m: ParsedCreatorMetrics) {
    const ageRows = m.age_distribution_overall ?? [];
    let ageMin: number | null = null, ageMax: number | null = null;
    if (ageRows.length > 0) {
      const sorted = [...ageRows].sort((a, b) => parseInt(a.label) - parseInt(b.label));
      let cum = 0;
      for (const r of sorted) {
        const parts = r.label.replace('+', '-99').split('-');
        const lo = parseInt(parts[0]), hi = parseInt(parts[1] ?? parts[0]);
        if (ageMin === null) ageMin = lo;
        ageMax = hi;
        cum += r.followers_pct ?? 0;
        if (cum >= 0.75) break;
      }
    }
    const gd = m.gender_distribution;
    const genderAffinity = (gd?.female ?? 0) > 0.55 ? 'female' : (gd?.male ?? 0) > 0.55 ? 'male' : 'unisex';
    const geoTopCountries = (m.geo_distribution ?? []).filter(r => r.followers_pct != null).map(r => ({ country: r.label, pct: r.followers_pct! }));
    return {
      followers: m.followers,
      engagement_rate: m.engagement_rate,
      avg_likes: m.avg_likes,
      avg_views: m.avg_views,
      primary_category: m.primary_category,
      secondary_categories: m.secondary_categories,
      audience_age_min: ageMin,
      audience_age_max: ageMax,
      audience_gender_affinity: genderAffinity,
      geo_top_countries: geoTopCountries,
      creator_interests: m.creator_interests,
      creator_brand_affinity: m.creator_brand_affinity,
      gender_distribution: m.gender_distribution,
      age_distribution_overall: m.age_distribution_overall,
      age_distribution_male: m.age_distribution_male,
      age_distribution_female: m.age_distribution_female,
      geo_distribution: m.geo_distribution,
      interests_ranked: m.interests_ranked,
      brand_affinity_ranked: m.brand_affinity_ranked,
      popular_hashtags: m.popular_hashtags,
      popular_mentions: m.popular_mentions,
      parseWarnings: m.parseWarnings,
    };
  }

  const enabledPlatforms = platformInputs.filter(p => p.enabled && p.parsedMetrics);
  const canSubmitUrl = profileUrl.trim().length > 0;
  const canSubmitPdf = enabledPlatforms.length > 0 && enabledPlatforms.every(p => p.handle.trim() && p.parsedMetrics?.followers);

  // ── URL/handle analysis ─────────────────────────────────────
  const handleUrlAnalyze = useCallback(async () => {
    if (!profileUrl.trim()) { setError('Enter a profile URL or handle'); return; }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiFetch<AnalyzeResult>('/analyze', {
        method: 'POST',
        body: JSON.stringify({
          mode: 'modash',
          profile_url: profileUrl.trim(),
          force_refresh: forceRefresh,
        }),
      });
      setResult(res);
      addToast('Match complete — top 5 products found', 'success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
      addToast('Analysis failed', 'error');
    } finally {
      setLoading(false);
    }
  }, [profileUrl, forceRefresh, addToast]);

  // ── PDF import analysis (fallback) ──────────────────────────
  const handlePdfAnalyze = useCallback(async () => {
    const ready = platformInputs.filter(p => p.enabled && p.parsedMetrics);
    if (ready.length === 0) { setError('At least one platform with a parsed PDF is required'); return; }
    for (const p of ready) {
      if (!p.handle.trim()) { setError(`${PLATFORM_META[p.platform]?.label}: Handle could not be detected — edit it manually`); return; }
      if (!p.parsedMetrics?.followers) { setError(`${PLATFORM_META[p.platform]?.label}: Followers count is required`); return; }
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      if (ready.length === 1) {
        const p = ready[0];
        const pUrl = PLATFORM_URL_TEMPLATES[p.platform]?.(p.handle.trim()) ?? '';
        const res = await apiFetch<AnalyzeResult>('/analyze', {
          method: 'POST',
          body: JSON.stringify({
            mode: 'pdf_import',
            profile_url: pUrl,
            platform: p.platform,
            handle: p.handle.trim(),
            metrics: buildMetricsPayload(p.parsedMetrics!),
          }),
        });
        setResult(res);
      } else {
        const res = await apiFetch<MultiPlatformAnalyzeResult>('/analyze-multi', {
          method: 'POST',
          body: JSON.stringify({
            platforms: ready.map(p => ({
              platform: p.platform,
              profile_url: PLATFORM_URL_TEMPLATES[p.platform]?.(p.handle.trim()) ?? '',
              handle: p.handle.trim(),
              metrics: buildMetricsPayload(p.parsedMetrics!),
            })),
          }),
        });
        setResult(res);
      }
      addToast(`Match complete — top 5 products found${ready.length > 1 ? ` (${ready.length} platforms)` : ''}`, 'success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
      addToast('Analysis failed', 'error');
    } finally {
      setLoading(false);
    }
  }, [platformInputs, addToast]);

  async function handleExportPdf() {
    if (!result) return;
    addToast('Generating PDF…', 'info');
    try {
      const { exportMatchReport } = await import('../lib/exportReport');
      const primaryMetrics = isMultiResult(result)
        ? platformInputs.find(p => p.enabled && p.parsedMetrics)?.parsedMetrics ?? null
        : platformInputs.find(p => p.enabled && p.parsedMetrics)?.parsedMetrics ?? null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await exportMatchReport(result as any, primaryMetrics);
      addToast('PDF exported successfully', 'success');
    } catch (err) {
      addToast('PDF export failed — ' + (err instanceof Error ? err.message : 'unknown error'), 'error');
    }
  }

  // Generate the user-facing report (full premium variant for admins) and
  // open the new /reports/:id viewer. This is the canonical experience the
  // creator sees — admins should be able to spot-check any creator at the
  // highest level before rolling out scoring or template changes.
  async function handleOpenFullReport() {
    if (!result) return;
    const profileUrl = isMultiResult(result)
      ? result.platforms?.[0]?.creator?.profile_url
      : (result as AnalyzeResult).creator?.profile_url;
    if (!profileUrl) {
      addToast('Could not resolve creator profile URL', 'error');
      return;
    }
    addToast('Generating full creator report…', 'info');
    try {
      const { reportsApi } = await import('../lib/reportsApi');
      const res = await reportsApi.generate({ profile_url: profileUrl });
      addToast('Report ready — opening viewer', 'success');
      navigate(`/admin/reports/${res.data.report_id}`);
    } catch (err) {
      addToast('Failed to generate report — ' + (err instanceof Error ? err.message : 'unknown error'), 'error');
    }
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      {/* Left: input panel */}
      <div className="space-y-4">
        {/* Mode toggle */}
        <div className="flex items-center gap-1 p-0.5" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}>
          <button
            onClick={() => setInputMode('url')}
            className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-medium transition-all"
            style={{
              backgroundColor: inputMode === 'url' ? 'rgba(255,212,0,0.08)' : 'transparent',
              color: inputMode === 'url' ? '#FFD400' : 'rgba(255,255,255,0.40)',
              border: inputMode === 'url' ? '1px solid rgba(255,212,0,0.18)' : '1px solid transparent',
              borderRadius: '2px',
            }}
          >
            <Globe className="w-3.5 h-3.5" /> Modash API
          </button>
          <button
            onClick={() => setInputMode('pdf')}
            className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-medium transition-all"
            style={{
              backgroundColor: inputMode === 'pdf' ? 'rgba(255,255,255,0.06)' : 'transparent',
              color: inputMode === 'pdf' ? 'rgba(255,255,255,0.80)' : 'rgba(255,255,255,0.30)',
              border: inputMode === 'pdf' ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
              borderRadius: '2px',
            }}
          >
            <FileText className="w-3.5 h-3.5" /> PDF Import
          </button>
        </div>

        {/* ── URL/Handle input (primary) ─────────────────────── */}
        {inputMode === 'url' && (
          <SectionCard title="Creator Profile" subtitle="Enter an Instagram, TikTok, or YouTube profile URL or handle">
            <div className="space-y-3">
              <div className="relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
                <input
                  type="text"
                  value={profileUrl}
                  onChange={e => setProfileUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && canSubmitUrl) void handleUrlAnalyze(); }}
                  placeholder="https://www.instagram.com/username or @username"
                  className="input w-full py-3 pl-10 text-sm"
                />
              </div>
              {detectedPlatform && (
                <div className="flex items-center gap-2">
                  <PlatformBadge platform={detectedPlatform.toLowerCase()} />
                  <span className="text-xs text-white/40">Auto-detected: {detectedPlatform}</span>
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox" checked={forceRefresh}
                  onChange={e => setForceRefresh(e.target.checked)}
                  className="accent-[#FFD400] w-3.5 h-3.5"
                />
                <span className="text-xs text-white/35">Force refresh (skip cache)</span>
              </label>
              <div className="flex items-start gap-2 p-2.5 text-xs text-white/30"
                style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}>
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-white/20" />
                <span>Uses Modash AI Search to find the creator, then fetches full audience demographics and brand collaboration history. ~1.35 credits per analysis.</span>
              </div>
            </div>
          </SectionCard>
        )}

        {/* ── PDF import (fallback) ──────────────────────────── */}
        {inputMode === 'pdf' && (
          <SectionCard title="Platforms" subtitle="Upload Modash PDFs — fallback when API is unavailable">
            <div className="space-y-3">
              {platformInputs.map(entry => {
                const meta = PLATFORM_META[entry.platform];
                const isParsing = parsing === entry.platform;
                return (
                  <div key={entry.platform} style={{ border: entry.enabled ? `1px solid ${meta.color}25` : '1px solid rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer transition-colors"
                      style={{ backgroundColor: entry.enabled ? `${meta.color}08` : 'rgba(255,255,255,0.02)' }}
                      onClick={() => { if (!entry.enabled) updatePlatform(entry.platform, { enabled: true }); }}
                    >
                      <div className="flex items-center gap-2">
                        <PlatformBadge platform={entry.platform} />
                        <span className="text-sm font-medium text-white">{meta.label}</span>
                        {entry.parsedMetrics && <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#4ade80' }} />}
                        {entry.parsedMetrics && <span className="text-xs text-white/30">{fmtNum(entry.parsedMetrics.followers ?? 0)} followers</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        {entry.enabled ? (
                          platformInputs.filter(p => p.enabled).length > 1 ? (
                            <button onClick={e => { e.stopPropagation(); updatePlatform(entry.platform, { enabled: false, pdfFile: null, parsedMetrics: null, handle: '' }); }} className="text-white/25 hover:text-white/60 transition-colors">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          ) : null
                        ) : (
                          <span className="text-xs font-medium" style={{ color: meta.color }}><Plus className="w-3.5 h-3.5 inline" /> Add</span>
                        )}
                      </div>
                    </div>
                    <AnimatePresence>
                      {entry.enabled && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                          <div className="p-3 space-y-3" style={{ borderTop: `1px solid ${meta.color}15` }}>
                            <div
                              className={`relative transition-all cursor-pointer ${isParsing ? 'pointer-events-none' : ''}`}
                              style={{ border: '1px dashed rgba(255,255,255,0.12)', backgroundColor: 'rgba(255,255,255,0.015)', borderRadius: '2px' }}
                              onClick={() => !isParsing && fileRefs.current[entry.platform]?.click()}
                              onDragOver={e => e.preventDefault()}
                              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) void processFile(entry.platform, f); }}
                            >
                              <div className="p-4 text-center">
                                {isParsing ? (
                                  <div className="flex items-center justify-center gap-2" style={{ color: '#FFD400' }}>
                                    <Loader2 className="w-4 h-4 animate-spin" /><span className="text-xs font-medium">Parsing PDF…</span>
                                  </div>
                                ) : entry.pdfFile ? (
                                  <div className="flex items-center justify-center gap-2">
                                    <CheckCircle2 className="w-4 h-4" style={{ color: '#4ade80' }} />
                                    <span className="text-xs font-medium text-white">{entry.pdfFile.name}</span>
                                    <span className="text-[10px] text-white/30">· Click to replace</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center gap-2">
                                    <Upload className="w-4 h-4 text-white/30" />
                                    <span className="text-xs text-white/45">Drop Modash PDF or click to upload</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <input ref={el => { fileRefs.current[entry.platform] = el; }} type="file" accept=".pdf" className="hidden"
                              onChange={async e => { const f = e.target.files?.[0]; if (f) await processFile(entry.platform, f); }} />
                            {entry.parsedMetrics && (
                              <div className="flex items-center gap-2">
                                <label className="text-[10px] font-semibold text-white/35 uppercase tracking-wider shrink-0">Handle</label>
                                <div className="flex items-center gap-1.5 flex-1">
                                  <span className="text-xs text-white/30">@</span>
                                  <input type="text" value={entry.handle} onChange={e => updatePlatform(entry.platform, { handle: e.target.value.replace(/^@/, '') })} placeholder="handle" className="input text-sm flex-1" />
                                  {entry.handle && <span className="text-[9px] font-semibold uppercase shrink-0" style={{ color: '#FFD400' }}>auto-detected</span>}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="flex items-start gap-2 p-3 text-red-400 text-sm"
              style={{ backgroundColor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: '2px' }}
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Run button */}
        <button
          onClick={inputMode === 'url' ? handleUrlAnalyze : handlePdfAnalyze}
          disabled={loading || (inputMode === 'url' ? !canSubmitUrl : !canSubmitPdf)}
          className="btn-primary w-full py-3 text-sm"
        >
          <Sparkles className="w-4 h-4" />
          {inputMode === 'url'
            ? 'Analyze Creator'
            : enabledPlatforms.length > 1
              ? `Generate Combined Report (${enabledPlatforms.length} platforms)`
              : 'Generate Report'
          }
        </button>
      </div>

      {/* Right: loading → results → empty */}
      <div>
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <MatchingOverlay onCancel={() => setLoading(false)} />
            </motion.div>
          ) : result ? (
            <motion.div key="results" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
              <ResultsPanel
                result={result}
                onExportPdf={handleExportPdf}
                onOpenFullReport={handleOpenFullReport}
              />
            </motion.div>
          ) : (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center h-full min-h-[420px] text-center">
              <div className="w-14 h-14 flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px' }}>
                <BarChart3 className="w-6 h-6 text-white/20" />
              </div>
              <p className="text-sm font-medium text-white/40">Results will appear here</p>
              <p className="text-xs text-white/20 mt-1">Enter a profile URL and click Analyze Creator</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Toasts */}
      <ToastContainer>
        {toasts.map(t => <Toast key={t.id} message={t.message} variant={t.variant} onDismiss={() => removeToast(t.id)} />)}
      </ToastContainer>
    </div>
  );
}

// ── Products Tab ──────────────────────────────────────────────────────────

function ProductsTab() {
  const [products, setProducts]     = useState<ProductItem[]>([]);
  const [total, setTotal]           = useState(0);
  const [offset, setOffset]         = useState(0);
  const [vertical, setVertical]     = useState('');
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [enriching, setEnriching]   = useState(false);
  const [msg, setMsg]               = useState<string | null>(null);
  const [error, setError]           = useState<string | null>(null);
  const [catalogImgErrors, setCatalogImgErrors] = useState<Set<number>>(new Set());
  const fileRef = useRef<HTMLInputElement>(null);
  const LIMIT = 50;
  const VERTICALS = ['', 'apparel', 'beauty', 'home', 'sports', 'accessories', 'other'];

  const fetchProducts = useCallback(async (newOffset = offset, newVertical = vertical, newSearch = search) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(newOffset) });
      if (newVertical) params.set('vertical', newVertical);
      if (newSearch)   params.set('search', newSearch);
      const d = await apiFetch<{ products: ProductItem[]; total: number }>(`/products?${params}`);
      setProducts(d.products);
      setTotal(d.total);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
    finally { setLoading(false); }
  }, [offset, vertical, search]);

  useEffect(() => { void fetchProducts(0, '', ''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMsg(null);
    setError(null);
    const isJson = file.name.endsWith('.json');
    const text = await file.text();
    try {
      const res = await apiFetch<{ inserted: number; skipped: number; errors: number }>('/products/upload', {
        method: 'POST',
        headers: { 'Content-Type': isJson ? 'application/json' : 'text/csv' },
        body: text,
      });
      setMsg(`Inserted: ${res.inserted}  Skipped: ${res.skipped}  Errors: ${res.errors}`);
    } catch (err) { setError(err instanceof Error ? err.message : 'Upload failed'); }
    finally { setUploading(false); }
  }

  async function handleEnrich() {
    setEnriching(true);
    setMsg(null);
    try {
      const res = await apiFetch<{ enriched: number; errors: number; total_processed: number }>('/products/enrich?limit=200', { method: 'POST' });
      setMsg(`Enriched: ${res.enriched}  Errors: ${res.errors}`);
      void fetchProducts();
    } catch (err) { setError(err instanceof Error ? err.message : 'Enrichment failed'); }
    finally { setEnriching(false); }
  }

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SectionCard title="Upload Products" subtitle="JSON array or CSV with headers">
          <input ref={fileRef} type="file" accept=".json,.csv" className="hidden" onChange={handleUpload} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-secondary w-full text-sm">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Choose File</>}
          </button>
          {msg && <p className="text-xs text-mint-400 mt-2">{msg}</p>}
        </SectionCard>
        <SectionCard title="Enrich Raw Products" subtitle="Run heuristic or AI enrichment on unprocessed items">
          <button onClick={handleEnrich} disabled={enriching} className="btn-secondary w-full text-sm">
            {enriching ? <><Loader2 className="w-4 h-4 animate-spin" /> Enriching…</> : <><Sparkles className="w-4 h-4" /> Enrich Next 200</>}
          </button>
        </SectionCard>
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select value={vertical} onChange={e => { setVertical(e.target.value); void fetchProducts(0, e.target.value, search); setOffset(0); }} className="input py-2 w-auto text-sm">
          {VERTICALS.map(v => <option key={v} value={v}>{v || 'All verticals'}</option>)}
        </select>
        <input
          type="search" placeholder="Search title…" value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { void fetchProducts(0, vertical, search); setOffset(0); } }}
          className="input py-2 text-sm flex-1 min-w-[200px]"
        />
        <button onClick={() => { void fetchProducts(0, vertical, search); setOffset(0); }} className="btn-secondary text-sm py-2">Search</button>
      </div>

      {/* Table */}
      <SectionCard noPadding subtitle={`${total.toLocaleString()} products`}>
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-white/25" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="table-header">
                <tr>
                  {['', 'ID', 'Title', 'Vertical', 'Category', 'Price', 'Tags', 'I', 'M', 'P'].map(h => (
                    <th key={h} className="text-left text-[11px] font-semibold text-white/35 px-4 py-3 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id} className="table-row">
                    <td className="px-3 py-2.5">
                      {p.primary_image_url && !catalogImgErrors.has(p.id) ? (
                        <img src={proxyImageUrl(p.primary_image_url)!} alt=""
                          className="w-9 h-9 object-cover"
                          style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px' }}
                          onError={() => setCatalogImgErrors(prev => new Set([...prev, p.id]))} />
                      ) : (
                        <div className="w-9 h-9 flex items-center justify-center"
                          style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '2px' }}>
                          <Package className="w-4 h-4 text-white/25" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/25 text-xs">{p.id}</td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="truncate font-medium text-white/80">{p.title}</p>
                      {p.subcategory && <p className="text-xs text-white/30">{p.subcategory}</p>}
                    </td>
                    <td className="px-4 py-3 text-white/45 text-xs">{p.vertical}</td>
                    <td className="px-4 py-3 text-white/45 text-xs">{p.category}</td>
                    <td className="px-4 py-3 text-white/65 whitespace-nowrap text-xs">${p.price_min}–${p.price_max}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {p.tags.slice(0, 3).map(t => (
                          <span key={t} className="px-1.5 py-0.5 text-[10px]"
                            style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.40)', borderRadius: '2px' }}>
                            {t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-xs font-bold text-white/55">{p.impulse_score}</td>
                    <td className="px-4 py-3 text-center text-xs font-bold text-white/55">{p.margin_score}</td>
                    <td className="px-4 py-3 text-center text-xs font-bold text-white/55">{p.proof_score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > LIMIT && (
          <div className="flex justify-between items-center px-4 py-3"
            style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            <button disabled={offset === 0} onClick={() => { const o = Math.max(0, offset - LIMIT); setOffset(o); void fetchProducts(o); }} className="btn-ghost text-sm py-1.5 disabled:opacity-30">← Previous</button>
            <span className="text-xs text-white/30">{offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
            <button disabled={offset + LIMIT >= total} onClick={() => { const o = offset + LIMIT; setOffset(o); void fetchProducts(o); }} className="btn-ghost text-sm py-1.5 disabled:opacity-30">Next →</button>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ── Calibration Tab ───────────────────────────────────────────────────────

function CalibrationTab() {
  const [calibRows, setCalibRows]     = useState<CalibRow[]>([]);
  const [, setConfigs]                = useState<ScoringConfig[]>([]);
  const [loading, setLoading]         = useState(true);
  const [editingCalib, setEditingCalib] = useState<Partial<CalibRow> | null>(null);
  const [weightsStr, setWeightsStr]   = useState('');
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState<string | null>(null);
  const [error, setError]             = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      apiFetch<{ rows: CalibRow[] }>('/calibration'),
      apiFetch<{ configs: ScoringConfig[] }>('/scoring-config'),
    ]).then(([c, s]) => {
      setCalibRows(c.rows);
      setConfigs(s.configs);
      if (s.configs[0]) setWeightsStr(JSON.stringify(s.configs[0].weights, null, 2));
    }).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function saveCalibRow() {
    if (!editingCalib) return;
    setSaving(true);
    setMsg(null);
    try {
      if (editingCalib.id) {
        await apiFetch(`/calibration/${editingCalib.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ conv_low: editingCalib.conv_low, conv_expected: editingCalib.conv_expected, conv_high: editingCalib.conv_high }),
        });
      } else {
        await apiFetch('/calibration', { method: 'POST', body: JSON.stringify(editingCalib) });
      }
      const { rows } = await apiFetch<{ rows: CalibRow[] }>('/calibration');
      setCalibRows(rows);
      setEditingCalib(null);
      setMsg('Saved');
    } catch (e) { setError(e instanceof Error ? e.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  async function saveWeights() {
    setSaving(true);
    setMsg(null);
    try {
      const weights = JSON.parse(weightsStr);
      await apiFetch('/scoring-config', { method: 'PATCH', body: JSON.stringify({ weights }) });
      setMsg('Weights saved');
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to save weights'); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-white/25" /></div>;

  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-red-400">{error}</p>}
      {msg   && <p className="text-sm text-mint-400">{msg}</p>}

      <SectionCard title="Scoring Weights" subtitle='Keys: category, tags, age, price, margin, complexity (penalty), proof — values 0.00–1.00'>
        <textarea
          rows={12} value={weightsStr} onChange={e => setWeightsStr(e.target.value)}
          className="input w-full font-mono text-xs resize-none"
        />
        <button onClick={saveWeights} disabled={saving} className="btn-secondary mt-3 text-sm">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : 'Save Weights'}
        </button>
      </SectionCard>

      <SectionCard
        title="GMV Calibration Table"
        actions={<button onClick={() => setEditingCalib({ platform: 'instagram', follower_tier: 'micro', engagement_tier: 'mid', category: 'general', geo_bias: 'global' })} className="btn-ghost text-xs py-1.5 flex items-center gap-1.5"><Plus className="w-3 h-3" /> Add Row</button>}
        noPadding
      >
        {editingCalib && (
          <div className="p-4 border-b" style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.06)' }}>
            <p className="text-[11px] font-semibold text-white/35 uppercase tracking-wider mb-3">{editingCalib.id ? 'Edit Row' : 'New Row'}</p>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {(['platform', 'follower_tier', 'engagement_tier', 'category', 'geo_bias'] as const).filter(() => !editingCalib.id).map(field => (
                <div key={field}>
                  <label className="text-[11px] text-white/35 mb-1 block uppercase tracking-wider">{field}</label>
                  <input className="input py-1.5 text-xs" value={(editingCalib as Record<string, unknown>)[field] as string ?? ''}
                    onChange={e => setEditingCalib(p => ({ ...p, [field]: e.target.value }))} />
                </div>
              ))}
              {(['conv_low', 'conv_expected', 'conv_high'] as const).map(k => (
                <div key={k}>
                  <label className="text-[11px] text-white/35 mb-1 block uppercase tracking-wider">{k}</label>
                  <input type="number" step="0.000001" className="input py-1.5 text-xs"
                    value={editingCalib[k] ?? ''}
                    onChange={e => setEditingCalib(p => ({ ...p, [k]: parseFloat(e.target.value) }))} />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={saveCalibRow} disabled={saving} className="btn-primary text-xs py-1.5">{saving ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setEditingCalib(null)} className="btn-ghost text-xs py-1.5">Cancel</button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="table-header">
              <tr>
                {['Platform', 'Tier', 'Engagement', 'Category', 'Geo', 'Conv Low', 'Expected', 'Conv High', ''].map(h => (
                  <th key={h} className="text-left text-[11px] font-semibold text-white/35 px-4 py-3 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {calibRows.map(row => (
                <tr key={row.id} className="table-row">
                  <td className="px-4 py-3 text-white/75">{row.platform}</td>
                  <td className="px-4 py-3 text-white/50">{row.follower_tier}</td>
                  <td className="px-4 py-3 text-white/50">{row.engagement_tier}</td>
                  <td className="px-4 py-3 text-white/50">{row.category}</td>
                  <td className="px-4 py-3 text-white/50">{row.geo_bias}</td>
                  <td className="px-4 py-3 font-mono text-white/40">{row.conv_low.toFixed(6)}</td>
                  <td className="px-4 py-3 font-mono font-semibold" style={{ color: '#FFD400' }}>{row.conv_expected.toFixed(6)}</td>
                  <td className="px-4 py-3 font-mono text-white/40">{row.conv_high.toFixed(6)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setEditingCalib(row)} className="text-white/35 hover:text-white transition-colors text-xs">Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

const TABS = [
  { label: 'Analyze', icon: <Sparkles className="w-4 h-4" /> },
  { label: 'Products', icon: <Package className="w-4 h-4" /> },
  { label: 'Calibration', icon: <BarChart3 className="w-4 h-4" /> },
];

export default function AdminCreatorMatch() {
  const [tab, setTab] = useState(0);
  const navigate = useNavigate();
  const { isDemoMode } = useDemo();

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        title="Creator Match"
        subtitle="Analyze creator profiles → match to products → estimate GMV"
        back={{ label: 'Back to Dashboard', onClick: () => navigate('/admin/dashboard') }}
        actions={
          <div className="flex items-center gap-2">
            <a href="/admin/explore" className="btn-ghost text-sm flex items-center gap-1.5 py-2">
              <FileText className="w-4 h-4" /> History
            </a>
          </div>
        }
      />

      {/* Tab bar */}
      <div className="flex mb-6 gap-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {TABS.map((t, i) => (
          <button
            key={t.label}
            onClick={() => setTab(i)}
            className="flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all mr-1"
            style={{
              borderBottom: i === tab ? '2px solid #FFD400' : '2px solid transparent',
              color: i === tab ? '#ffffff' : 'rgba(255,255,255,0.40)',
              marginBottom: '-1px',
            }}
          >
            <span style={{ color: i === tab ? '#FFD400' : 'rgba(255,255,255,0.30)' }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.15 }}
        >
          {tab === 0 && <AnalyzeTab demoMode={isDemoMode} />}
          {tab === 1 && <ProductsTab />}
          {tab === 2 && <CalibrationTab />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
